import React, {MouseEventHandler, ReactElement} from 'react';
import {SortableContainer, SortableContainerProps, SortableElement, SortableHandle} from 'react-sortable-hoc';
import Select, {components, MultiValueGenericProps, MultiValueProps} from 'react-select';
import {ReactSelectProps} from 'config/react-select'
import {GroupBase} from 'react-select/dist/declarations/src/types'

const SortableMultiValue = SortableElement((props: MultiValueProps) => {
	// this prevents the menu from being opened/closed when the user clicks
	// on a value to begin dragging it. ideally, detecting a click (instead of
	// a drag) would still focus the control and toggle the menu, but that
	// requires some magic with refs that are out of scope for this example
	const onMouseDown: MouseEventHandler<HTMLDivElement> = (e) => {
		e.preventDefault();
		e.stopPropagation();
	};
	const innerProps = {...props.innerProps, onMouseDown};
	return <components.MultiValue {...props} innerProps={innerProps}/>;
})
const SortableMultiValueLabel = SortableHandle(
	({children, ...props}: MultiValueGenericProps) => <components.MultiValueLabel {...props} >
		<span dangerouslySetInnerHTML={{__html: children + ''}}/>
	</components.MultiValueLabel>,
)

const WrappedSelect = SortableContainer(Select)

export type SortableMultiSelectComponents<Option, Group extends GroupBase<Option> = GroupBase<Option>> = Omit<ReactSelectProps<Option, true, Group>['components'], 'MultiValue' | 'MultiValueLabel'>

export type SortableMultiSelectProps<
	Option,
	Group extends GroupBase<Option> = GroupBase<Option>
> = Omit<ReactSelectProps<Option, true, Group>, 'components'> & SortableContainerProps & {
	components?: SortableMultiSelectComponents<Option, Group>
}

export type SortableMultiSelectType<
	Option = unknown,
	Group extends GroupBase<Option> = GroupBase<Option>
> = <
	Option = unknown,
	Group extends GroupBase<Option> = GroupBase<Option>
>(
	props: SortableMultiSelectProps<Option, Group>,
) => ReactElement

/**
 * example at https://react-select.com/advanced#sortable-multiselect
 *
 * use onSortEndCallback with
 * 	import {arrayMove} from 'react-sortable-hoc'
 */
export function SortableMultiSelect<
	Option,
	IsMulti extends boolean = true,
	Group extends GroupBase<Option> = GroupBase<Option>
>(props: SortableMultiSelectProps<Option, Group>) {
	const {axis = "xy", distance = 4} = props;
	const WS = WrappedSelect as unknown as SortableMultiSelectType<Option, Group>
	return <WS
		{...props}
		isMulti={true}
		// react-sortable-hoc props:
		axis={axis}
		distance={distance}
		// small fix for https://github.com/clauderic/react-sortable-hoc/pull/352:
		getHelperDimensions={({node}: any) => node.getBoundingClientRect()}
		// @ts-ignore We're failing to provide a required index prop to SortableElement [copied from react-select example]
		components={{
			...props.components,
			MultiValue: SortableMultiValue,
			MultiValueLabel: SortableMultiValueLabel,
		}}
	/>
}

/** use this in onChange to correctly set array order (new value first) */
export function onChangeSortableMultiSelectValueHandler<T>(
	value: T[],
	action: Parameters<NonNullable<ReactSelectProps<T, true>['onChange']>>[1]['action'],
	maxAmount: number = 10,
) {
	if (!value) value = [];
	if (action === 'select-option' && value.length > 1) {
		value = [value[value.length - 1], ...value.slice(0, Math.min(maxAmount, value.length) - 1)]
	}
	return value
}